import { PianoRenderer } from "@/lib/engine/components/piano-renderer";
import { PianoLayout, type KeyLayout } from "@/lib/piano/piano-layout";
import { Piano } from "@/lib/piano/piano";
import { Viewport } from "@/lib/piano/piano-viewport";
import { Note, NoteRange } from "@/lib/music/note";
import { Container } from "pixi.js";
import {
  HorizontalProjection,
  whiteKeyWidthBoundsFor,
  type WhiteKeyWidthBounds,
  BLACK_KEY_RATIO,
  KEYBOARD_HEIGHT_PER_WHITE_KEY,
} from "@/lib/piano/horizontal-projection";
import { WITNESS_MIN_WHITE_KEY_WIDTH } from "../witness-keyboard";

/**
 * Média « pointeur principal grossier » (doigt, stylet), mémorisé une fois.
 *
 * Un `MediaQueryList` est vivant : `.matches` suit les changements de média
 * sans réabonnement, on peut donc le relire à chaque frame sans coût. La
 * création est paresseuse parce que ce module est importé depuis du code rendu
 * côté serveur, où `window` n'existe pas.
 */
let coarsePointerMedia: MediaQueryList | null | undefined;

/**
 * Vrai quand l'appareil se pilote au doigt.
 *
 * C'est **le** critère de la largeur de touche, et pas un seuil de largeur : un
 * iPad en paysage fait 1024 à 1366 px de large, exactement la plage des postes
 * de bureau. Un critère de largeur le rangerait du côté « grand écran » et
 * raterait précisément l'appareil qu'on vise.
 *
 * Le changement de média est pris en compte au prochain layout (redimensionnement,
 * chargement, recentrage) plutôt que par un abonnement : le type de pointeur ne
 * change pas en cours de session hors branchement d'un périphérique, et un
 * relayout forcé depuis ici traverserait la frontière de responsabilité entre
 * la géométrie et la vue.
 */
function isCoarsePointer(): boolean {
  if (coarsePointerMedia === undefined) {
    coarsePointerMedia =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(pointer: coarse)")
        : null;
  }

  return coarsePointerMedia?.matches ?? false;
}

/** Time constant of the auto-follow exponential ease (ms). Larger = lazier. */
const AUTO_FOLLOW_TAU_MS = 260;

/**
 * After a manual pan, auto-follow stays suppressed for this long so it does
 * not immediately fight the user's chosen scroll position.
 */
const AUTO_FOLLOW_RESUME_MS = 2500;

/**
 * Margin applied to a piece's robust white-key count to derive the zoom
 * (`baseWhite`): ~5% extra, but always at least one white key on each side so
 * very small pieces keep a little breathing room.
 */
const BASE_WHITE_MARGIN_RATIO = 1.05;
const BASE_WHITE_MIN_EXTRA = 2;

/**
 * Empty white keys always kept on EACH side of the piece's note span inside the
 * rendered window, so the lowest/highest notes never sit flush against the
 * frame edge. Clamped away only at the very ends of the keyboard, where there
 * is physically no key beyond.
 */
const WINDOW_EDGE_MARGIN_WHITE = 1;

/**
 * PianoComponent: horizontal-projection authority + keyboard owner.
 *
 * It owns the shared {@link Viewport} / {@link PianoLayout} (note framing) and
 * derives the single horizontal projection (clamped key width + offset)
 * consumed by the keyboard *and* every piano-roll sublayer via
 * {@link getProjection}. This eliminates the former `width / whiteCount`
 * duplication and guarantees keyboard / roll alignment.
 *
 * Framing: the rendered window is NOT the full 88-key keyboard. It is a
 * `windowWhite`-key range centred on the piece (see {@link frameForPiece}) that
 * (a) always contains every note of the piece (strict min/max — correctness),
 * (b) is at least the tessitura-aligned `baseWhite`, and (c) is widened with
 * neighbouring keys to fill the canvas on large screens, up to the full 88.
 * Changing the window rebuilds the renderer (different set of keys); a piece is
 * then brought into view by panning within that window.
 *
 * Sizing (zoom): the white-key width is ALIGNED ON `baseWhite` (the piece's
 * robust tessitura + ~15%), capped at MAX and floored at MIN:
 *   `whiteKeyWidth = clamp(canvasWidth / baseWhite, MIN, MAX)`.
 * - narrow piece / wide screen -> hits MAX (keys at their target size); the
 *   surplus width shows neighbouring keys (window widening), then borders;
 * - wide piece -> keys shrink so the whole tessitura fits without scrolling;
 * - very wide piece / small screen -> floored at MIN, the rest reached by pan.
 * The zoom stays on `baseWhite`, NOT on `windowWhite`: the fill keys never
 * shrink the piece's zoom.
 * MIN et MAX ne sont ni constants ni universels, ils dépendent du pointeur
 * (voir `whiteKeyWidthBoundsFor`) :
 * - pointeur fin : MAX descend sur un canvas étroit pour garder ~26 touches
 *   blanches à l'écran, ce qui ne concerne que les morceaux à tessiture
 *   étroite — les autres sont déjà sous le plafond ;
 * - pointeur grossier : le plancher monte à une vraie taille de cible tactile
 *   et devient la borne active pour la quasi-totalité du répertoire. Le régime
 *   « fit » disparaît alors presque toujours au profit du régime « scroll » :
 *   c'est l'auto-follow, pas le zoom, qui amène les notes sous les doigts.
 *
 * Offset model:
 * - when the 88-key content fits, it is centred with borders (offset >= 0);
 *   otherwise it is scrollable and `panOffsetX` (<= 0) pans it (manual gestures
 *   + the edge-triggered auto-follow).
 *
 * The keyboard draws its keys in content space (offset 0) and the whole
 * keyboard container is translated by the offset (cheap, no per-key redraw on
 * pan); the roll bakes the offset into each sublayer's x (it repaints every
 * frame anyway). Both read the same key width and the same offset value.
 */
export class PianoComponent {
  private renderer: PianoRenderer;
  private _viewport: Viewport;
  private _layout: PianoLayout;
  private rendererSize = { width: 0, height: 0 };

  /**
   * Horizontal scroll position as a screen-px translation. Negative when the
   * content is panned right into a piece wider than the canvas. Only
   * meaningful while {@link isScrollable}; re-centred on (re)load.
   */
  private panOffsetX = 0;
  /** False until the first geometry computation seeds `panOffsetX`. */
  private panInitialized = false;
  /** Wall-clock timestamp until which auto-follow stays suppressed. */
  private autoFollowSuspendedUntil = 0;
  /**
   * White-key count the key width (zoom) is aligned on: the current piece's
   * robust tessitura + margin (`baseWhite`), or the full keyboard when no piece
   * is loaded. Independent of the rendered window width.
   */
  private baseWhiteCount: number;
  /**
   * Strict pitch span (every note of the piece) the rendered window MUST
   * contain, or null when no piece is loaded (window = full keyboard).
   */
  private pieceStrict: { min: number; max: number } | null = null;
  /** MIDI the rendered window is centred on (centre of the robust tessitura). */
  private pieceCenterMidi: number;
  /** White-key MIDIs of the full keyboard, ascending. Drives window framing. */
  private readonly worldWhiteMidis: number[];
  /**
   * Le clavier n'est plus qu'un témoin : on le regarde s'allumer, on n'y joue
   * pas. Posé par {@link StudioView.doLayout} à chaque layout ; voir
   * `witness-keyboard.ts` pour la condition et ce qu'elle change.
   */
  private witness = false;

  constructor(private stage: Container, private piano: Piano) {
    // The renderer starts framing the full keyboard; a piece narrows the window
    // via frameForPiece(). The roll pans within the current window.
    this._viewport = new Viewport(this.piano, this.piano.getRange());
    this._layout = new PianoLayout(this.piano, this._viewport);
    this.renderer = new PianoRenderer(this.stage, this.piano, this._layout);
    this.worldWhiteMidis = this.computeWorldWhiteMidis();
    this.baseWhiteCount = this.fullWhiteCount;
    this.pieceCenterMidi = Math.round(
      (this.piano.getRange().from.toMidi() + this.piano.getRange().to.toMidi()) / 2,
    );
  }

  /**
   * Reset to the default view: window = full keyboard, key width aligned on it,
   * scroll re-centred. Used when no piece is loaded.
   */
  resetViewport(): void {
    this.baseWhiteCount = this.fullWhiteCount;
    this.pieceStrict = null;
    this.pieceCenterMidi = Math.round(
      (this.piano.getRange().from.toMidi() + this.piano.getRange().to.toMidi()) / 2,
    );
    this.updateWindow();
    this.recenterPan();
  }

  /**
   * Déclare que ce clavier est un témoin, et non un instrument.
   *
   * Le plancher tactile de largeur de touche tombe alors : il n'existe que
   * pour qu'un doigt puisse viser une touche, et sans doigt à loger il ne fait
   * que réduire la musique visible à deux octaves et demie. Les bornes
   * « pointeur fin » reprennent, qui visent ~26 touches blanches.
   *
   * À appeler **avant** `setSize`, qui en dépend : le drapeau ne fait que se
   * poser, c'est le `setSize` suivant qui rebâtit la géométrie.
   */
  setWitness(witness: boolean): void {
    this.witness = witness;
  }

  setSize(width: number, height: number) {
    this.rendererSize = { width, height };

    // The window width depends on the canvas (it widens with the screen), so
    // recompute it here and rebuild the renderer if the key set changed.
    this.updateWindow();

    const whiteKeyWidth = this.computeWhiteKeyWidth();
    const contentWidth = this.computeContentWidth(whiteKeyWidth);

    if (!this.panInitialized) {
      this.panOffsetX = this.centeringOffset(contentWidth);
      this.panInitialized = true;
    } else if (this.isScrollable(contentWidth)) {
      // Resize keeps the user's pan intent; just re-clamp into the new range.
      this.panOffsetX = this.clampPan(this.panOffsetX, contentWidth);
    } else {
      this.panOffsetX = this.centeringOffset(contentWidth);
    }

    this.renderer.setGeometry(whiteKeyWidth, height);
  }

  build() {
  }

  public draw() {
    // Translate the whole keyboard container by the shared offset (keys are
    // drawn in content space). Same offset value the roll bakes into its x.
    this.stage.x = this.getOffsetX();
    this.renderer.draw();
  }

  /**
   * Shared horizontal projection (pitch -> screen-x), consumed by the keyboard
   * and every piano-roll sublayer. Rebuilt each call from the current geometry
   * and offset; cheap (three numbers).
   */
  getProjection(): HorizontalProjection {
    const whiteKeyWidth = this.computeWhiteKeyWidth();
    const blackKeyWidth = Math.round(whiteKeyWidth * BLACK_KEY_RATIO);

    return new HorizontalProjection(
      whiteKeyWidth,
      blackKeyWidth,
      this.effectiveOffsetX(whiteKeyWidth),
    );
  }

  /** Resolved horizontal offset for the current frame (allocation-free). */
  getOffsetX(): number {
    return this.effectiveOffsetX(this.computeWhiteKeyWidth());
  }

  /**
   * Keyboard strip height for a given available width, at constant key aspect:
   * the `baseWhite`-clamped white-key width (the zoom, NOT the window width)
   * times {@link KEYBOARD_HEIGHT_PER_WHITE_KEY}. Pure (no mutation): called by
   * {@link StudioView.doLayout} to size the containers before `setSize`, so the
   * strip height tracks the key width.
   *
   * **La hauteur reste calée sur les bornes « pointeur fin », même au doigt.**
   * C'est le seul découplage du fichier, et il est délibéré : sans lui, élargir
   * les touches à 40 px monterait la bande de 117 à 147 px, prise sur le
   * piano-roll. Or sur un iPhone en paysage il ne reste que ~256 px de canvas
   * une fois header et toolbar déduits — le roll, qui est la surface de
   * lecture, n'en garderait presque rien. Et la bande n'a besoin de grandir ni
   * pour ressembler à un clavier, ni pour être une cible verticale confortable
   * (117 px = 22 mm sur iPad, une cible d'axe Y jamais limitante). La
   * jouabilité au doigt se joue sur la largeur, pas sur la hauteur.
   *
   * Deux conséquences assumées : au doigt les touches sont plus trapues (ratio
   * ~2,9 au lieu de 3,67), et la hauteur de bande est **exactement** celle
   * d'aujourd'hui à largeur de canvas égale, sur tout appareil.
   */
  keyboardHeightForWidth(width: number): number {
    const driverWidth = this.clampToBounds(width, whiteKeyWidthBoundsFor(width, false));

    return Math.round(driverWidth * KEYBOARD_HEIGHT_PER_WHITE_KEY);
  }

  /** Whether the content overflows the canvas (pan / auto-follow possible). */
  get scrollable(): boolean {
    return this.isScrollable(this.computeContentWidth(this.computeWhiteKeyWidth()));
  }

  /**
   * Manual horizontal pan by a screen-px delta. No-op when the content fits.
   * Suspends auto-follow briefly so it does not fight the gesture.
   */
  panBy(deltaPx: number): void {
    const whiteKeyWidth = this.computeWhiteKeyWidth();
    const contentWidth = this.computeContentWidth(whiteKeyWidth);

    if (!this.isScrollable(contentWidth)) {
      return;
    }

    this.panOffsetX = this.clampPan(this.panOffsetX + deltaPx, contentWidth);
    this.panInitialized = true;
    this.autoFollowSuspendedUntil = performance.now() + AUTO_FOLLOW_RESUME_MS;
  }

  /**
   * Edge-triggered horizontal follow with minimal, oscillation-free movement.
   *
   * Holds the view still while the pitch range [minMidi, maxMidi] stays in
   * frame — notes resting against an edge are fine. When an edge leaves the
   * frame, it eases by the minimum needed to bring that edge back, *clamped so
   * the opposite edge stays in view*. Because the target always lies in the
   * interval that keeps BOTH edges visible, revealing one side can never push
   * the other out — so notes on both sides no longer cause left/right thrashing.
   *
   * No-op when the content fits, while a recent manual pan suppresses the
   * follow, or when the range is wider than the viewport (then no pan can show
   * all of it, so we hold rather than thrash).
   */
  autoFollowToRange(minMidi: number, maxMidi: number, deltaMS: number): void {
    const whiteKeyWidth = this.computeWhiteKeyWidth();
    const contentWidth = this.computeContentWidth(whiteKeyWidth);

    if (!this.isScrollable(contentWidth)) {
      return;
    }

    if (performance.now() < this.autoFollowSuspendedUntil) {
      return;
    }

    const lowKey = this._layout.getKeyByMidi(minMidi);
    const highKey = this._layout.getKeyByMidi(maxMidi);

    if (!lowKey || !highKey) {
      return;
    }

    // Content-space extent of the range (offset 0), via the shared projection
    // so the math matches what is actually drawn (white/black snapping).
    const proj = new HorizontalProjection(
      whiteKeyWidth,
      Math.round(whiteKeyWidth * BLACK_KEY_RATIO),
      0,
    );
    const rangeLeft = proj.keyLeft(lowKey);
    const rangeRight = proj.keyLeft(highKey) + proj.keyWidth(highKey);

    const width = this.rendererSize.width;

    // Range wider than the viewport: hold — no pan can reveal all of it, so
    // moving would only cost the player their bearings.
    if (rangeRight - rangeLeft > width) {
      return;
    }

    // Pan interval that keeps BOTH range edges in view: pan >= -rangeLeft (left
    // edge in) and pan <= width - rangeRight (right edge in). Non-empty since
    // the range fits. The target is the current pan clamped into it -> minimal
    // movement, and never pushes the opposite edge out.
    const loPan = -rangeLeft;
    const hiPan = width - rangeRight;

    let target: number;

    if (this.panOffsetX < loPan) {
      target = loPan;
    } else if (this.panOffsetX > hiPan) {
      target = hiPan;
    } else {
      // Both edges already in frame: hold (no lateral movement).
      return;
    }

    target = this.clampPan(target, contentWidth);

    const alpha = 1 - Math.exp(-deltaMS / AUTO_FOLLOW_TAU_MS);
    this.panOffsetX += (target - this.panOffsetX) * alpha;
    this.panInitialized = true;
  }

  /**
   * Adopt a piece's framing:
   * - zoom (key width) is aligned on the robust tessitura + ~15% (`baseWhite`),
   *   so it fills the width up to MAX or shrinks to fit down to MIN;
   * - the rendered window is sized/positioned to ALWAYS contain every note
   *   (strict min/max), be at least `baseWhite` wide, and widen to fill the
   *   canvas — then the renderer is rebuilt and the scroll re-centred.
   *
   * @param robustMin/robustMax  Trimmed tessitura driving the zoom.
   * @param strictMin/strictMax  Absolute pitch span; the window must contain it.
   */
  frameForPiece(robustMin: number, robustMax: number, strictMin: number, strictMax: number): void {
    this.baseWhiteCount = this.baseWhiteCountFor(robustMin, robustMax);
    this.pieceStrict = { min: strictMin, max: strictMax };
    this.pieceCenterMidi = Math.round((robustMin + robustMax) / 2);
    this.updateWindow();
    this.centerPanOnMidi(this.pieceCenterMidi);
  }

  /**
   * White-key count the zoom is aligned on: the robust tessitura's white-key
   * count plus ~15% (at least one key each side), clamped to the keyboard.
   */
  private baseWhiteCountFor(minMidi: number, maxMidi: number): number {
    const robustWhite = this.whiteCountBetween(minMidi, maxMidi);
    const padded = Math.max(
      Math.round(robustWhite * BASE_WHITE_MARGIN_RATIO),
      robustWhite + BASE_WHITE_MIN_EXTRA,
    );

    return Math.max(1, Math.min(this.fullWhiteCount, padded));
  }

  // ── window framing ───────────────────────────────────────────────

  /** Full-keyboard white-key count (52 on a standard 88-key piano). */
  private get fullWhiteCount(): number {
    return this.worldWhiteMidis.length;
  }

  /** Ascending list of every white-key MIDI on the full keyboard. */
  private computeWorldWhiteMidis(): number[] {
    const world = this.piano.getRange();
    const midis: number[] = [];

    for (let midi = world.from.toMidi(); midi <= world.to.toMidi(); midi++) {
      if (PianoComponent.isWhiteMidi(midi)) {
        midis.push(midi);
      }
    }

    return midis;
  }

  /** White keys in the inclusive MIDI range [minMidi, maxMidi]. */
  private whiteCountBetween(minMidi: number, maxMidi: number): number {
    let count = 0;

    for (const midi of this.worldWhiteMidis) {
      if (midi >= minMidi && midi <= maxMidi) {
        count++;
      }
    }

    return count;
  }

  private static isWhiteMidi(midi: number): boolean {
    const pc = ((midi % 12) + 12) % 12;

    return !(pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10);
  }

  /**
   * Recompute the rendered window for the current piece/size and, when the key
   * set actually changes, push it to the viewport, recompute the layout and
   * rebuild the renderer. Idempotent: a no-op while the window is unchanged, so
   * continuous resizes do not trigger useless rebuilds.
   */
  private updateWindow(): void {
    const range = this.computeWindowRange();
    const current = this._viewport.window;

    if (
      range.from.toMidi() === current.from.toMidi() &&
      range.to.toMidi() === current.to.toMidi()
    ) {
      return;
    }

    this._viewport.setWindow(range);
    this._layout.recompute();
    this.rebuildRenderer();
  }

  /**
   * MIDI range of the window to render: `windowWhite` white keys centred on the
   * piece, shifted to contain [strictMin, strictMax], clamped to the keyboard.
   * Falls back to the full keyboard when no piece is loaded.
   */
  private computeWindowRange(): NoteRange {
    const world = this.piano.getRange();

    if (!this.pieceStrict) {
      return world;
    }

    const { min: strictMin, max: strictMax } = this.pieceStrict;
    const keyWidth = this.computeWhiteKeyWidth();
    const fitWhite = Math.floor(this.rendererSize.width / keyWidth);
    const strictWhite = this.whiteCountBetween(strictMin, strictMax);
    // Reserve one empty white key on each side of the note span so nothing
    // ever renders flush against the frame edge.
    const need = Math.max(
      this.baseWhiteCount,
      strictWhite + 2 * WINDOW_EDGE_MARGIN_WHITE,
    );
    const windowWhite = Math.min(this.fullWhiteCount, Math.max(need, fitWhite));

    return this.windowRangeForWhiteCount(windowWhite, strictMin, strictMax);
  }

  /**
   * Build a MIDI range of exactly `windowWhite` white keys, centred on
   * {@link pieceCenterMidi} but shifted so it still contains [strictMin,
   * strictMax], clamped inside the keyboard. White keys are non-uniform in MIDI,
   * so we index into {@link worldWhiteMidis} rather than computing offsets.
   */
  private windowRangeForWhiteCount(windowWhite: number, strictMin: number, strictMax: number): NoteRange {
    const whites = this.worldWhiteMidis;
    const total = whites.length;

    let centerIdx = 0;
    let bestDist = Infinity;

    for (let i = 0; i < total; i++) {
      const dist = Math.abs(whites[i] - this.pieceCenterMidi);

      if (dist < bestDist) {
        bestDist = dist;
        centerIdx = i;
      }
    }

    // Last white at/below strictMin and first white at/above strictMax: the
    // window MUST straddle both so no note is clipped out of the render.
    let strictLowIdx = 0;
    let strictHighIdx = total - 1;

    for (let i = 0; i < total; i++) {
      if (whites[i] <= strictMin) {
        strictLowIdx = i;
      }

      if (whites[total - 1 - i] >= strictMax) {
        strictHighIdx = total - 1 - i;
      }
    }

    // Widen the mandatory span by the edge margin so the window keeps an empty
    // white key on each side of the notes (clamped at the keyboard ends). The
    // `+ 2 * WINDOW_EDGE_MARGIN_WHITE` in `computeWindowRange` guarantees
    // `windowWhite` is large enough to hold this widened span.
    strictLowIdx = Math.max(0, strictLowIdx - WINDOW_EDGE_MARGIN_WHITE);
    strictHighIdx = Math.min(total - 1, strictHighIdx + WINDOW_EDGE_MARGIN_WHITE);

    // Centre-preferred start, clamped so the window contains the strict span and
    // stays inside the keyboard. windowWhite >= strictWhite guarantees a valid
    // interval.
    const lowerBound = Math.max(0, strictHighIdx - windowWhite + 1);
    const upperBound = Math.min(total - windowWhite, strictLowIdx);
    const preferred = centerIdx - Math.floor((windowWhite - 1) / 2);
    const start = Math.max(lowerBound, Math.min(upperBound, preferred));

    return new NoteRange(
      Note.fromMidi(whites[start]),
      Note.fromMidi(whites[start + windowWhite - 1]),
    );
  }

  /**
   * Recreate the renderer in place after a window change: the set of keys
   * differs, so the old key views are dropped and rebuilt from the recomputed
   * layout. Geometry is re-applied immediately so a draw between this and the
   * next setSize still paints.
   */
  private rebuildRenderer(): void {
    this.renderer.cleanup();
    this.stage.removeChildren();
    this.renderer = new PianoRenderer(this.stage, this.piano, this._layout);
    this.renderer.setGeometry(this.computeWhiteKeyWidth(), this.rendererSize.height);
  }

  // ── horizontal geometry helpers ──────────────────────────────────

  /** Pan so `midi` sits at the horizontal centre of the canvas (clamped). */
  private centerPanOnMidi(midi: number): void {
    const whiteKeyWidth = this.computeWhiteKeyWidth();
    const contentWidth = this.computeContentWidth(whiteKeyWidth);

    if (!this.isScrollable(contentWidth)) {
      this.recenterPan();

      return;
    }

    const key = this._layout.getKeyByMidi(midi);

    if (!key) {
      this.recenterPan();

      return;
    }

    this.panOffsetX = this.clampPan(
      this.rendererSize.width / 2 - this.contentCenterOfKey(key, whiteKeyWidth),
      contentWidth,
    );
    this.panInitialized = true;
  }

  /**
   * Content-space horizontal centre of a key (offset 0), mirroring the
   * HorizontalProjection snapping (round in content space, offset added later).
   */
  private contentCenterOfKey(key: KeyLayout, whiteKeyWidth: number): number {
    if (key.color === "white") {
      return (
        Math.round(key.whiteIndex * whiteKeyWidth) +
        Math.round((key.whiteIndex + 1) * whiteKeyWidth)
      ) / 2;
    }

    return Math.round((key.whiteIndex + 1) * whiteKeyWidth);
  }

  private get whiteCount(): number {
    return Math.max(1, this._layout.getWhiteCount());
  }

  /**
   * White-key width, aligned on `baseWhite` (the zoom) and clamped to
   * [MIN, MAX]. See the class doc for the regimes (fill-to-MAX, fit, scroll).
   */
  private computeWhiteKeyWidth(): number {
    return this.clampWhiteKeyWidth(this.rendererSize.width);
  }

  /**
   * `baseWhite`-aligned white-key width for an available width, clamped aux
   * bornes du pointeur courant (voir {@link whiteKeyWidthBoundsFor}).
   *
   * Au doigt, c'est le **plancher** qui devient la borne active pour la quasi-
   * totalité du répertoire : la tessiture médiane du corpus vaut 27 touches
   * blanches, soit 36 px sur un canvas d'iPad — sous le plancher tactile. Le
   * clavier tactile est donc à taille de touche presque constante, et le
   * morceau se rejoint en défilant.
   *
   * Sauf en témoin ({@link setWitness}) : le doigt qu'on protégeait n'est pas
   * là, et le plancher ne ferait plus que rogner la musique visible. Il tombe
   * alors à {@link WITNESS_MIN_WHITE_KEY_WIDTH}, en dessous même du plancher
   * « pointeur fin » — qui est un plancher de *clic*, et on ne clique pas non
   * plus sur un témoin. Le **plafond** reste celui du pointeur fin : c'est lui
   * qui mord sur un écran large, et n'y toucher pas est ce qui garantit que
   * rien ne bouge là-bas.
   */
  private clampWhiteKeyWidth(width: number): number {
    const coarse = !this.witness && isCoarsePointer();
    const bounds = whiteKeyWidthBoundsFor(width, coarse);

    if (this.witness) {
      return this.clampToBounds(width, { min: WITNESS_MIN_WHITE_KEY_WIDTH, max: bounds.max });
    }

    return this.clampToBounds(width, bounds);
  }

  /**
   * Largeur de touche alignée sur `baseWhite`, bornée. Le plancher l'emporte
   * sur le plafond (ordre historique) : sur un canvas très étroit on préfère
   * des touches jouables qui débordent, le défilement fait le reste.
   */
  private clampToBounds(width: number, bounds: WhiteKeyWidthBounds): number {
    const raw = width / this.baseWhiteCount;

    return Math.max(bounds.min, Math.min(bounds.max, raw));
  }

  /** Scrollable content width = the rendered window's white keys at the zoom. */
  private computeContentWidth(whiteKeyWidth: number): number {
    return Math.round(this.whiteCount * whiteKeyWidth);
  }

  /** Translation that horizontally centres the content in the canvas. */
  private centeringOffset(contentWidth: number): number {
    return (this.rendererSize.width - contentWidth) / 2;
  }

  private isScrollable(contentWidth: number): boolean {
    return contentWidth > this.rendererSize.width + 0.5;
  }

  /** Clamp a pan offset into the valid scroll range [width - content, 0]. */
  private clampPan(offset: number, contentWidth: number): number {
    const minOffset = this.rendererSize.width - contentWidth;

    return Math.min(0, Math.max(minOffset, offset));
  }

  /** Resolved offset: centring when the content fits, clamped pan otherwise. */
  private effectiveOffsetX(whiteKeyWidth: number): number {
    const contentWidth = this.computeContentWidth(whiteKeyWidth);

    if (!this.isScrollable(contentWidth)) {
      return this.centeringOffset(contentWidth);
    }

    return this.clampPan(this.panOffsetX, contentWidth);
  }

  private recenterPan(): void {
    const contentWidth = this.computeContentWidth(this.computeWhiteKeyWidth());
    this.panOffsetX = this.centeringOffset(contentWidth);
    this.panInitialized = true;
  }

  /** Shared viewport (same note range for PianoRoll alignment) */
  get viewport(): Viewport {
    return this._viewport;
  }

  /** Shared layout (same geometry for PianoRoll alignment) */
  get layout(): PianoLayout {
    return this._layout;
  }

  async destroy() {
    await this.renderer?.destroy();
  }
}
